import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "Actor",
  "Actress",
  "Cinematographer",
  "Director of Photography",
  "Editor",
  "Music Director",
  "Writer",
  "Production Crew",
  "Other",
];

export const Portfolio = () => {
  const [form, setForm] = useState({
    name: "",
    role: "Actor",
    phone: "",
    email: "",
    links: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.role.trim() || !form.phone.trim()) {
      toast.error("Please fill name, role and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/portfolio`, form);
      setDone(true);
      toast.success("Portfolio received. The AU Movies team will reach out shortly.");
      setForm({ name: "", role: "Actor", phone: "", email: "", links: "", message: "" });
    } catch (err) {
      toast.error("Submission failed. Please try the email option below.");
    } finally {
      setSubmitting(false);
    }
  };

  const mailtoHref = (() => {
    const subject = encodeURIComponent(`Portfolio Submission — ${form.name || "[Your Name]"}`);
    const body = encodeURIComponent(
      `Hello AU Movies team,\n\nName: ${form.name}\nRole: ${form.role}\nPhone: ${form.phone}\nEmail: ${form.email}\nLinks: ${form.links}\n\n${form.message}\n\n— Sent via aumovies.in`
    );
    return `mailto:${SITE.email}?subject=${subject}&body=${body}`;
  })();

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative scroll-mt-nav py-28 md:py-36 bg-[#050505] overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#2DD4BF]/8 blur-[160px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase text-[#2DD4BF] mb-6">05 · Join the House</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-white">
              Share your <span className="italic text-gradient-aum">portfolio</span> with AU Movies.
            </h2>
            <div className="divider-aum my-8" />
            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
              Actors, cinematographers, writers, technicians — if you'd like to be considered for
              Rosha, Sangarsha or future productions, send us your work. Every submission is
              reviewed by the AU Movies team.
            </p>

            <div className="mt-8 space-y-4 text-sm text-slate-400">
              <p>· Submit the form for direct delivery to our database.</p>
              <p>· Or use the email button to send from your own inbox with attachments.</p>
              <p>· Include showreels, IMDb links or Drive folders in the Links field.</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="portfolio-form"
              className="glass-card rounded-3xl p-8 md:p-10 space-y-5"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="pf-name" className="text-slate-300 text-xs tracking-[0.18em] uppercase">
                    Full Name *
                  </Label>
                  <Input
                    id="pf-name"
                    data-testid="portfolio-input-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Jane Doe"
                    className="mt-2 bg-black/40 border-white/10 text-white placeholder:text-slate-500 focus:border-[#2DD4BF]"
                  />
                </div>
                <div>
                  <Label htmlFor="pf-role" className="text-slate-300 text-xs tracking-[0.18em] uppercase">
                    Role / Craft *
                  </Label>
                  <select
                    id="pf-role"
                    data-testid="portfolio-select-role"
                    value={form.role}
                    onChange={(e) => update("role", e.target.value)}
                    className="mt-2 w-full h-10 rounded-md bg-black/40 border border-white/10 text-white px-3 focus:outline-none focus:border-[#2DD4BF]"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r} className="bg-[#0A0D14]">
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="pf-phone" className="text-slate-300 text-xs tracking-[0.18em] uppercase">
                    Phone *
                  </Label>
                  <Input
                    id="pf-phone"
                    data-testid="portfolio-input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+91 98xxxxxxx"
                    className="mt-2 bg-black/40 border-white/10 text-white placeholder:text-slate-500 focus:border-[#2DD4BF]"
                  />
                </div>
                <div>
                  <Label htmlFor="pf-email" className="text-slate-300 text-xs tracking-[0.18em] uppercase">
                    Email
                  </Label>
                  <Input
                    id="pf-email"
                    type="email"
                    data-testid="portfolio-input-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@example.com"
                    className="mt-2 bg-black/40 border-white/10 text-white placeholder:text-slate-500 focus:border-[#2DD4BF]"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="pf-links" className="text-slate-300 text-xs tracking-[0.18em] uppercase">
                  Portfolio Links · Showreel · Drive
                </Label>
                <Input
                  id="pf-links"
                  data-testid="portfolio-input-links"
                  value={form.links}
                  onChange={(e) => update("links", e.target.value)}
                  placeholder="https://drive.google.com/... , https://imdb.com/..."
                  className="mt-2 bg-black/40 border-white/10 text-white placeholder:text-slate-500 focus:border-[#2DD4BF]"
                />
              </div>

              <div>
                <Label htmlFor="pf-message" className="text-slate-300 text-xs tracking-[0.18em] uppercase">
                  A short note about you
                </Label>
                <Textarea
                  id="pf-message"
                  data-testid="portfolio-textarea-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us about your craft, experience and what excites you about AU Movies..."
                  rows={4}
                  className="mt-2 bg-black/40 border-white/10 text-white placeholder:text-slate-500 focus:border-[#2DD4BF]"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="submit"
                  data-testid="portfolio-submit-btn"
                  disabled={submitting}
                  className="btn-aum justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending
                    </>
                  ) : done ? (
                    <>
                      <CheckCircle2 size={16} /> Sent · Send Another
                    </>
                  ) : (
                    <>
                      <Send size={16} /> Submit Portfolio
                    </>
                  )}
                </button>

                <a
                  href={mailtoHref}
                  data-testid="portfolio-mailto-btn"
                  className="btn-ghost justify-center"
                >
                  <Mail size={16} /> Email with attachments
                </a>
              </div>

              <p className="text-xs text-slate-500 pt-2">
                For attachments (resumes, headshots, showreels), use the email option — it opens
                your mail app pre‑filled to {SITE.email}.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
